/**
 * FERP Portal - Workflow State View Hook
 *
 * Fetches tasks and groups them by status for the board visualization.
 * Engine-driven: columns reflect actual task FSM state, not user arrangement.
 *
 * Supports a demo mode that overlays sample data for UI preview.
 *
 * Uses ListTasks query with client-side grouping (single round-trip).
 * For large datasets, switch to parallel GetTasksByStatus queries per column.
 */

import { useMemo, useCallback } from 'react';
import {
  useListTasksQuery,
  useGetTaskStatisticsQuery,
  GqlTaskStatus,
  GqlTaskPriority,
  type TaskFieldsFragment,
} from '@/generated/graphql';
import { useTenantId } from '@/auth';
import { useTaskSubscriptions } from './useRealTimeSubscriptions';

/** Column definition matching workflows/pm/board-layout config */
export interface BoardColumn {
  status: GqlTaskStatus;
  label: string;
  color: string;
  wipLimit: number | null;
  collapsed: boolean;
  tasks: TaskFieldsFragment[];
}

/** Statistics summary for the board header */
export interface BoardStats {
  total: number;
  active: number;
  blocked: number;
  completed: number;
  failed: number;
  slaApproaching: number;
  slaBreach: number;
}

export interface UseWorkflowStateViewReturn {
  columns: BoardColumn[];
  stats: BoardStats;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Column configuration derived from board-layout config values.
 * Uses GqlTaskStatus enum — no string literals.
 */
const COLUMN_CONFIG: Array<{
  status: GqlTaskStatus;
  label: string;
  color: string;
  wipLimit: number | null;
  collapsed: boolean;
}> = [
  { status: GqlTaskStatus.PENDING, label: 'Pending', color: 'gray', wipLimit: null, collapsed: false },
  { status: GqlTaskStatus.READY, label: 'Ready', color: 'blue', wipLimit: null, collapsed: false },
  { status: GqlTaskStatus.ASSIGNED, label: 'Assigned', color: 'indigo', wipLimit: null, collapsed: false },
  { status: GqlTaskStatus.IN_PROGRESS, label: 'In Progress', color: 'cyan', wipLimit: 10, collapsed: false },
  { status: GqlTaskStatus.BLOCKED, label: 'Blocked', color: 'orange', wipLimit: null, collapsed: false },
  { status: GqlTaskStatus.COMPLETED, label: 'Completed', color: 'green', wipLimit: null, collapsed: true },
  { status: GqlTaskStatus.FAILED, label: 'Failed', color: 'red', wipLimit: null, collapsed: true },
];

const ACTIVE_STATUSES = new Set<GqlTaskStatus>([
  GqlTaskStatus.PENDING,
  GqlTaskStatus.READY,
  GqlTaskStatus.ASSIGNED,
  GqlTaskStatus.IN_PROGRESS,
  GqlTaskStatus.PAUSED,
  GqlTaskStatus.PENDING_EXTERNAL,
]);

/** Priority sort order: CRITICAL first */
const PRIORITY_ORDER: Record<string, number> = {
  [GqlTaskPriority.CRITICAL]: 0,
  [GqlTaskPriority.HIGH]: 1,
  [GqlTaskPriority.NORMAL]: 2,
  [GqlTaskPriority.LOW]: 3,
};

function sortByPriority(a: TaskFieldsFragment, b: TaskFieldsFragment): number {
  return (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99);
}

function buildColumns(tasks: TaskFieldsFragment[]): BoardColumn[] {
  const grouped = new Map<GqlTaskStatus, TaskFieldsFragment[]>();

  for (const task of tasks) {
    const existing = grouped.get(task.status) ?? [];
    existing.push(task);
    grouped.set(task.status, existing);
  }

  return COLUMN_CONFIG.map((col) => ({
    ...col,
    tasks: (grouped.get(col.status) ?? []).sort(sortByPriority),
  }));
}

function buildStats(tasks: TaskFieldsFragment[]): BoardStats {
  let active = 0, blocked = 0, completed = 0, failed = 0;
  for (const t of tasks) {
    if (ACTIVE_STATUSES.has(t.status)) active++;
    if (t.status === GqlTaskStatus.BLOCKED) blocked++;
    if (t.status === GqlTaskStatus.COMPLETED) completed++;
    if (t.status === GqlTaskStatus.FAILED) failed++;
  }
  return { total: tasks.length, active, blocked, completed, failed, slaApproaching: 0, slaBreach: 0 };
}

export function useWorkflowStateView(demoTasks?: TaskFieldsFragment[]): UseWorkflowStateViewReturn {
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;
  const isLive = !demoTasks && !skip;

  const {
    data: taskData,
    loading: taskLoading,
    error: taskError,
    refetch: refetchTasks,
  } = useListTasksQuery({
    variables: { limit: 200 },
    skip: !isLive,
    pollInterval: 30_000,
  });

  const {
    data: statsData,
    loading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useGetTaskStatisticsQuery({ skip: !isLive });

  const refetch = useCallback(() => {
    if (isLive) {
      refetchTasks();
      refetchStats();
    }
  }, [isLive, refetchTasks, refetchStats]);

  // Real-time: refetch when subscription emits task updates.
  // Backend returns empty streams until Valkey pub/sub is wired;
  // once it emits, the board updates instantly (no code change needed).
  useTaskSubscriptions({
    skip: !isLive,
    onTaskUpdate: refetch,
  });

  const allTasks = useMemo(() => {
    if (demoTasks) return demoTasks;
    const items = taskData?.taskApiListTasks?.items ?? [];
    // Deduplicate by task ID — backend may return the same task
    // across paginated fetches or workflow-instance joins
    const seen = new Set<string>();
    return items.filter(t => {
      if (seen.has(t.id)) return false;
      seen.add(t.id);
      return true;
    });
  }, [demoTasks, taskData]);

  const columns = useMemo(() => buildColumns(allTasks), [allTasks]);

  const stats = useMemo<BoardStats>(() => {
    if (demoTasks) return buildStats(demoTasks);

    const s = statsData?.taskApiGetTaskStatistics;
    if (s) {
      return {
        total: s.pendingCount + s.inProgressCount + s.completedCount + s.failedCount,
        active: s.inProgressCount + s.pendingCount,
        blocked: allTasks.filter(t => t.status === GqlTaskStatus.BLOCKED).length,
        completed: s.completedCount,
        failed: s.failedCount,
        slaApproaching: 0,
        slaBreach: 0,
      };
    }

    return buildStats(allTasks);
  }, [demoTasks, allTasks, statsData]);

  return {
    columns,
    stats,
    loading: demoTasks ? false : (taskLoading || statsLoading),
    error: demoTasks ? null : (taskError ?? statsError ?? null),
    refetch,
  };
}
